import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useContext, useEffect, useLayoutEffect } from 'react';
import { Handle, Position, useConnection, useStoreApi, useUpdateNodeInternals, } from '@xyflow/react';
import { FlowMetaContext, slotOffsetPercent, } from '../FlowEditorContext';
import { installEditorAssets } from '../editor-styles';
// Anchor Handle is invisible — click target only; the dot + goo render as children.
const BASE_HANDLE_CLASS = '!h-2.5 !w-2.5 !border-0 !bg-transparent';
// Slot handles are opacity-0 but need a size so React Flow can measure them.
const SLOT_HANDLE_CLASS = '!h-2.5 !w-2.5 !border-0 !bg-primary';
// Pure proximity test; caller gates on `connection.inProgress`.
function isTargetProximal(nodeInfo, side, pointer) {
    const width = nodeInfo.measured?.width ?? 0;
    const height = nodeInfo.measured?.height ?? 0;
    const left = nodeInfo.position.x;
    const top = nodeInfo.position.y;
    const mid = side === 'left'
        ? { x: left, y: top + height / 2 }
        : { x: left + width / 2, y: top };
    const dist = Math.hypot(pointer.x - mid.x, pointer.y - mid.y);
    return dist < Math.max(width, height) * 1.5;
}
// Renders the anchor Handle + one invisible slot Handle per edge for a single node side.
export function MultiHandle({ nodeId, handleType, position, side, nodeSelected, }) {
    useEffect(installEditorAssets, []);
    const { sourceSlotsByNode, targetSlotsByNode } = useContext(FlowMetaContext);
    const updateNodeInternals = useUpdateNodeInternals();
    const isHorizontalSide = position === Position.Left || position === Position.Right;
    const slotMap = handleType === 'source' ? sourceSlotsByNode : targetSlotsByNode;
    const anchorPrefix = handleType === 'source' ? 'src-' : 'tgt-';
    const sidePrefix = `${anchorPrefix}${side}:`;
    const anchorId = `${anchorPrefix}${side}-connect`;
    const slots = (slotMap.get(nodeId) ?? []).filter(s => s.startsWith(sidePrefix));
    const count = slots.length;
    const slotFingerprint = slots.join(',');
    useLayoutEffect(() => {
        updateNodeInternals(nodeId);
    }, [nodeId, slotFingerprint, updateNodeInternals]);
    // On-demand nodeLookup read; a `useStore` subscription would re-render per pan frame.
    const storeApi = useStoreApi();
    const connection = useConnection();
    const dragActive = handleType === 'target' &&
        connection.inProgress &&
        !!connection.to &&
        connection.fromNode?.id !== nodeId;
    const nodeInfo = dragActive
        ? storeApi.getState().nodeLookup.get(nodeId)
        : undefined;
    const targetProximal = dragActive && nodeInfo && connection.to
        ? isTargetProximal(nodeInfo, side, connection.to)
        : false;
    const anchorVisible = handleType === 'source' ? nodeSelected : targetProximal;
    // True while the pointer is snapped to this anchor during a drag.
    const latching = connection.inProgress &&
        (connection.toHandle?.nodeId ?? connection.toNode?.id) === nodeId &&
        connection.toHandle?.id === anchorId;
    const outwardSign = side === 'right' || side === 'bottom' ? 1 : -1;
    const extend = anchorVisible ? 8 : 0;
    // Overshoots inward past the node border so the goo covers the attach seam.
    const stemLen = anchorVisible ? extend + 10 : 0;
    const sproutStyle = {
        opacity: anchorVisible || latching ? 1 : 0,
        translate: isHorizontalSide
            ? `${outwardSign * extend}px 0`
            : `0 ${outwardSign * extend}px`,
        transition: 'opacity 220ms cubic-bezier(.2,.8,.2,1), translate 220ms cubic-bezier(.2,.8,.2,1)',
        pointerEvents: anchorVisible ? 'all' : 'none',
    };
    // White stem + backing blob merged by the SVG goo filter; primary dot sits on top.
    const gooWrapperStyle = {
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        filter: anchorVisible ? 'url(#super-goo-stem)' : undefined,
    };
    // Oversized by 1.5px so a white halo rings the dot.
    const blobStyle = {
        position: 'absolute',
        inset: '-1.5px',
        borderRadius: 9999,
    };
    const stemThickness = 5;
    const stemStyle = {
        position: 'absolute',
        borderRadius: 9999,
        transition: 'width 220ms cubic-bezier(.2,.8,.2,1), height 220ms cubic-bezier(.2,.8,.2,1)',
        ...(isHorizontalSide
            ? {
                height: `${stemThickness}px`,
                top: `calc(50% - ${stemThickness / 2}px)`,
                width: `${stemLen}px`,
                ...(side === 'right'
                    ? { right: `calc(100% - 2px)` }
                    : { left: `calc(100% - 2px)` }),
            }
            : {
                width: `${stemThickness}px`,
                left: `calc(50% - ${stemThickness / 2}px)`,
                height: `${stemLen}px`,
                ...(side === 'bottom'
                    ? { bottom: `calc(100% - 2px)` }
                    : { top: `calc(100% - 2px)` }),
            }),
    };
    // Crisp primary dot above the goo; grows + tints orange on hover-latch.
    const dotStyle = {
        position: 'absolute',
        inset: 0,
        borderRadius: 9999,
        transform: latching ? 'scale(1.2)' : 'scale(1)',
        background: latching ? 'orange' : undefined,
        boxShadow: latching
            ? '0 0 0 2px rgba(255, 165, 0, 0.2)'
            : '0 0 0 0 rgba(255, 165, 0, 0)',
        transition: 'transform 160ms cubic-bezier(.2,.8,.2,1), background-color 160ms ease, box-shadow 160ms ease',
    };
    const invisibleStyle = {
        opacity: 0,
        pointerEvents: 'none',
    };
    // Anchor keyed on `anchorId` so slot churn mid-drag can't remount it.
    return (_jsxs(Fragment, { children: [_jsxs(Handle, { type: handleType, position: position, id: anchorId, className: BASE_HANDLE_CLASS, style: sproutStyle, isConnectable: anchorVisible, children: [_jsxs("div", { "aria-hidden": true, style: gooWrapperStyle, children: [_jsx("div", { className: "bg-background", style: blobStyle }), stemLen > 0 && _jsx("div", { className: "bg-background", style: stemStyle })] }), _jsx("div", { "aria-hidden": true, className: "bg-primary", style: dotStyle })] }, anchorId), slots.map((slotId, index) => {
                const offsetPercent = slotOffsetPercent(index, count);
                const posStyle = isHorizontalSide
                    ? { top: `${offsetPercent}%` }
                    : { left: `${offsetPercent}%` };
                return (_jsx(Handle, { type: handleType, position: position, id: slotId, style: { ...posStyle, ...invisibleStyle }, isConnectable: false, className: SLOT_HANDLE_CLASS }, slotId));
            })] }));
}
//# sourceMappingURL=MultiHandle.js.map